import React from 'react'

// ─── Eyebrow ────────────────────────────────────────────────────
export interface EyebrowProps {
  children: React.ReactNode
  className?: string
}

/**
 * Eyebrow -- JetBrains Mono, uppercase, small, burnt orange.
 * Used above hero heading and section intros.
 */
export const Eyebrow: React.FC<EyebrowProps> = ({
  children,
  className = '',
}) => (
  <div
    className={`font-mono text-eg-xs font-medium uppercase tracking-[2px] text-eg-accent mb-5 ${className}`}
  >
    {children}
  </div>
)

// ─── Hero Heading ────────────────────────────────────────────────
export interface HeroHeadingProps {
  children: React.ReactNode
  className?: string
}

/**
 * HeroHeading -- Fraunces 56px black weight, tight leading.
 * Deep forest green with -0.03em letter-spacing.
 */
export const HeroHeading: React.FC<HeroHeadingProps> = ({
  children,
  className = '',
}) => (
  <h1
    className={`font-heading text-eg-7xl font-black leading-[1.08] tracking-[-0.03em] text-eg-primary mb-7 ${className}`}
  >
    {children}
  </h1>
)

// ─── Hero Emphasis ──────────────────────────────────────────────
export interface HeroEmProps {
  children: React.ReactNode
  className?: string
}

/**
 * HeroEm -- italic, light weight (300), burnt orange color.
 * Used inside HeroHeading for emphasis words.
 */
export const HeroEm: React.FC<HeroEmProps> = ({
  children,
  className = '',
}) => (
  <em className={`italic font-light text-eg-accent ${className}`}>
    {children}
  </em>
)

// ─── Section Title ──────────────────────────────────────────────
export interface SectionTitleProps {
  children: React.ReactNode
  align?: 'left' | 'center'
  className?: string
}

/**
 * SectionTitle -- Fraunces 42px bold, forest primary.
 */
export const SectionTitle: React.FC<SectionTitleProps> = ({
  children,
  align = 'left',
  className = '',
}) => (
  <h2
    className={`font-heading text-eg-5xl font-bold leading-[1.1] tracking-[-0.02em] text-eg-primary mb-4 ${align === 'center' ? 'text-center' : ''} ${className}`}
  >
    {children}
  </h2>
)

// ─── Section Subtitle ───────────────────────────────────────────
export interface SectionSubProps {
  children: React.ReactNode
  align?: 'left' | 'center'
  className?: string
}

export const SectionSub: React.FC<SectionSubProps> = ({
  children,
  align = 'left',
  className = '',
}) => (
  <p
    className={`text-eg-lg text-eg-text-secondary leading-[1.7] ${align === 'center' ? 'text-center mx-auto' : ''} max-w-[520px] ${className}`}
  >
    {children}
  </p>
)

// ─── Body Text ──────────────────────────────────────────────────
export interface BodyTextProps {
  children: React.ReactNode
  size?: 'sm' | 'base' | 'lg'
  muted?: boolean
  className?: string
}

export const BodyText: React.FC<BodyTextProps> = ({
  children,
  size = 'base',
  muted = false,
  className = '',
}) => {
  const sizeMap = {
    sm: 'text-eg-sm',
    base: 'text-eg-base',
    lg: 'text-eg-xl',
  }
  const color = muted ? 'text-eg-text-muted' : 'text-eg-text-secondary'

  return (
    <p className={`font-body leading-[1.65] ${sizeMap[size]} ${color} ${className}`}>
      {children}
    </p>
  )
}

// ─── Mono Text ──────────────────────────────────────────────────
export interface MonoTextProps {
  children: React.ReactNode
  size?: 'xs' | 'sm' | 'base'
  className?: string
}

export const MonoText: React.FC<MonoTextProps> = ({
  children,
  size = 'sm',
  className = '',
}) => {
  const sizeMap = { xs: 'text-eg-2xs', sm: 'text-eg-xs', base: 'text-eg-sm' }

  return (
    <span className={`font-mono ${sizeMap[size]} text-eg-text-muted ${className}`}>
      {children}
    </span>
  )
}

// ─── Card Heading ───────────────────────────────────────────────
export interface CardHeadingProps {
  children: React.ReactNode
  className?: string
}

export const CardHeading: React.FC<CardHeadingProps> = ({
  children,
  className = '',
}) => (
  <h3
    className={`font-heading text-eg-2xl font-bold text-eg-text mb-2.5 ${className}`}
  >
    {children}
  </h3>
)
